/**
 * What an apply ends with, for the alert at the top of the selected template's
 * card (components/ApplyResultAlert), and what a group apply shows while it
 * runs, in the apply overlay over that card (App.renderApplyScrim).
 *
 * An alert: { tone: 'success' | 'warning' | 'danger', title, summary,
 *             failures: [{ level, name, reason }], notice, note }
 * Nothing is rolled back on a partial apply, so the alert says what exists.
 * `notice` is a sentence that says what to do (UPDATES_NOT_APPROVED), or null.
 */

/** When monday refused create_update for a missing scope: why, and who can fix it. */
export const UPDATES_NOT_APPROVED =
  'Updates were not posted. Posting updates is new in this version of the app and needs a permission that hasn’t been approved yet. ' +
  'Ask an admin of your monday account to approve the app’s new permissions from the banner at the top of this view.';

const STAGE_LABEL = {
  preparing: 'Checking the board…',
  group: 'Creating the group…',
  items: 'Creating items…',
  subitems: 'Creating subitems…',
  updates: 'Posting updates…',
  done: 'Finishing…',
};

const n = (x) => (x === undefined || x === null ? 0 : x);
const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/** True while a group run is going ('done' without a result is still finishing). */
export function groupRunActive(run) {
  if (!run) return false;
  if (run.stage === 'error') return false;
  return run.stage !== 'done' || !run.result;
}

/** The overlay's line and bar for a running group apply: { text, pct }. */
export function groupProgressText(run) {
  const { stage, done, total } = run || {};
  const label = STAGE_LABEL[stage] || 'Working…';
  return { text: total ? `${label} ${n(done)} of ${total}` : label, pct: total ? Math.round((n(done) / total) * 100) : 0 };
}

/** monday's reason for a failed row, from whatever the apply recorded. */
export function reasonText(errors) {
  if (!errors) return 'monday gave no reason';
  if (typeof errors === 'string') return errors;
  if (Array.isArray(errors)) return (errors[0] && errors[0].message) || 'monday gave no reason';
  return errors.message || String(errors);
}

/** The result of applyGroupTemplate → alert. */
export function groupApplyResult(templateName, result) {
  const { counts, failures = [], groupTitle, updatesRefused = false } = result || {};
  const c = counts || { items: {}, subitems: {}, updates: {} };
  const parts = [`${n(c.items.created)} of ${plural(n(c.items.planned), 'item')}`];
  if (n(c.subitems.planned)) parts.push(`${n(c.subitems.created)} of ${plural(n(c.subitems.planned), 'subitem')}`);
  if (n(c.updates.planned)) parts.push(`${n(c.updates.posted)} of ${plural(n(c.updates.planned), 'update')}`);
  return {
    tone: failures.length || updatesRefused ? 'warning' : 'success',
    title: `“${groupTitle || templateName}” was added to the bottom of the board`,
    summary: `${parts.join(' · ')}. Close this item to see the group.`,
    failures,
    notice: updatesRefused ? UPDATES_NOT_APPROVED : null,
    note: failures.length ? 'Nothing else was rolled back.' : null,
  };
}

/**
 * The subitem apply's tallies → alert.
 * failures / updateFailures: [{ name, errors }] as executeApply records them;
 * updatesRefused: monday refused create_update for a missing scope.
 */
export function subitemApplyResult({
  templateName,
  total,
  failures = [],
  updatesPlanned = 0,
  updatesPosted = 0,
  updateFailures = [],
  updatesRefused = false,
}) {
  const created = total - failures.length;
  const rows = [
    ...failures.map((f) => ({ level: 'subitem', name: f.name, reason: reasonText(f.errors) })),
    ...updateFailures.map((f) => ({ level: 'update', name: f.name, reason: reasonText(f.errors) })),
  ];
  if (total > 0 && created === 0) {
    return {
      tone: 'danger',
      title: `Could not apply “${templateName}”`,
      summary: 'None of its subitems were created: this board does not accept some of the template’s values. Review the template and try again.',
      failures: rows,
      notice: null,
      note: null,
    };
  }
  const parts = [`${created} of ${plural(total, 'subitem')}`];
  if (updatesPlanned) parts.push(`${updatesPosted} of ${plural(updatesPlanned, 'update')}`);
  return {
    tone: rows.length || updatesRefused ? 'warning' : 'success',
    title: `“${templateName}” was applied to this item`,
    summary: `${parts.join(' · ')}.`,
    failures: rows,
    notice: updatesRefused ? UPDATES_NOT_APPROVED : null,
    note: failures.length
      ? 'Some template values are not accepted by this board: review the template and try again. Nothing else was rolled back.'
      : rows.length
        ? 'Nothing else was rolled back.'
        : null,
  };
}

/** An apply that stopped with an error → alert. */
export function applyErrorResult(templateName, message) {
  return { tone: 'danger', title: `Could not apply “${templateName}”`, summary: message, failures: [], notice: null, note: null };
}

/** How a failed row is named in the alert. */
export const failureLabel = (f) => (f.level === 'update' ? `Update on “${f.name}”` : `${f.name} (${f.level})`);
